import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as timesheetsApi from '../../api/timesheets';
import * as clientsApi from '../../api/clients';
import * as projectsApi from '../../api/projects';
import { formatDate } from '../../utils/format';

const emptyForm = { clientId: '', projectId: '', workDate: '', hoursWorked: '', notes: '' };

export default function Timesheets() {
  const { actor } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [clientOptions, setClientOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    timesheetsApi
      .listTimesheets({})
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load timesheets.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ensureOptionsLoaded = async () => {
    if (clientOptions.length === 0) {
      const res = await clientsApi.listClients({ limit: 500 });
      setClientOptions(res.data.map((c) => ({ value: c.id, label: c.clientName })));
    }
    if (projectOptions.length === 0) {
      const res = await projectsApi.listProjects({ limit: 500 });
      setProjectOptions(res.data.map((p) => ({ value: p.id, label: p.projectName })));
    }
  };

  const openCreate = async () => {
    setForm(emptyForm);
    setEditingId(null);
    await ensureOptionsLoaded();
    setShowForm(true);
  };

  const openEdit = async (row) => {
    setForm({
      clientId: row.clientId || '',
      projectId: row.projectId || '',
      workDate: row.workDate ? row.workDate.slice(0, 10) : '',
      hoursWorked: row.hoursWorked,
      notes: row.notes || '',
    });
    setEditingId(row.id);
    await ensureOptionsLoaded();
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await timesheetsApi.updateTimesheet(editingId, form);
        setSuccess('Time entry updated.');
      } else {
        await timesheetsApi.createTimesheet(form);
        setSuccess('Time entry logged.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save time entry.');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await timesheetsApi.deleteTimesheet(row.id);
      setSuccess('Time entry deleted.');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete entry.');
    }
  };

  const canActOnRow = (r) => r.employeeId === actor.profile?.id || actor.role !== 'employee';

  const columns = [
    { key: 'workDate', label: 'Date', render: (r) => formatDate(r.workDate) },
    { key: 'employee', label: 'Employee', render: (r) => r.employee?.name || '-' },
    { key: 'client', label: 'Client', render: (r) => r.client?.clientName || '-' },
    { key: 'project', label: 'Project', render: (r) => r.project?.projectName || '-' },
    { key: 'hoursWorked', label: 'Hours' },
    { key: 'notes', label: 'Notes', render: (r) => r.notes || '-' },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => canActOnRow(r) && (
        <div className="btn-group">
          <button type="button" className="btn-link" onClick={() => openEdit(r)}>Edit</button>
          <button type="button" className="btn-link" onClick={() => handleDelete(r)}>Delete</button>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Timesheets">
      <div className="page-header">
        <div>
          <h1 className="heading">{actor.role === 'employee' ? 'My Timesheet' : 'Timesheets'}</h1>
          <p className="page-subtext">Hours logged against clients and projects.</p>
        </div>
        <button type="button" className="btn" onClick={openCreate}>Log Time</button>
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      {loading ? <Loader text="Loading timesheets..." /> : <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No time entries logged yet." />}

      {showForm && (
        <Modal
          title={editingId ? 'Edit Time Entry' : 'Log Time Entry'}
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="ts-form" className="btn">{editingId ? 'Save Changes' : 'Save'}</button>
            </>
          }
        >
          <form id="ts-form" onSubmit={handleSave}>
            <div className="form-grid single-column">
              <FormField label="Date" type="date" value={form.workDate} onChange={(e) => setForm({ ...form, workDate: e.target.value })} required />
              <FormField label="Hours Worked" type="number" step="0.25" min="0" value={form.hoursWorked} onChange={(e) => setForm({ ...form, hoursWorked: e.target.value })} required />
              <FormField label="Client" type="select" options={clientOptions} value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} />
              <FormField label="Project" type="select" options={projectOptions} value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })} />
              <FormField label="Notes" type="textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}